// Cvanto AI — client for /api/cvanto-ai (server/routes/cvantoAI.js). The
// browser never talks to a model or to marketplace tables: it only calls this
// backend, which owns authorization, quota, tools and truth. Sending a message
// streams Server-Sent Events over a POST (fetch + ReadableStream — EventSource
// cannot send an Authorization header or a body).
import { getAccessToken } from "../../awsAuth";

const API_BASE =
  process.env.REACT_APP_API_BASE ||
  (typeof window !== "undefined" && window.location.hostname.includes("onrender.com")
    ? "https://cvanto-api.onrender.com"
    : "http://localhost:5000");

const BASE = `${API_BASE}/api/cvanto-ai`;

export class CvantoAIError extends Error {
  constructor(message, { code = "UNKNOWN", status = 0, data = null } = {}) {
    super(message);
    this.name = "CvantoAIError";
    this.code = code;
    this.status = status;
    this.data = data;
  }
}

const CODE_MESSAGES = {
  UNAUTHENTICATED: "Увійдіть в акаунт, щоб користуватися Cvanto AI",
  NOT_ENTITLED: "Cvanto AI недоступний для вашого акаунта",
  RATE_LIMITED: "Забагато запитів. Спробуйте, будь ласка, трохи пізніше",
  TOO_MANY_STREAMS: "Зачекайте, поки завершиться попередня відповідь",
};

async function authHeaders(extra = {}) {
  const token = await getAccessToken().catch(() => null);
  return { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...extra };
}

async function toError(response) {
  const data = await response.json().catch(() => null);
  const code = data?.code || (response.status === 401 ? "UNAUTHENTICATED" : "HTTP_ERROR");
  return new CvantoAIError(data?.error || CODE_MESSAGES[code] || "Не вдалося виконати запит", {
    code,
    status: response.status,
    data,
  });
}

async function request(path, { method = "GET", body, signal } = {}) {
  let response;

  try {
    response = await fetch(`${BASE}${path}`, {
      method,
      headers: await authHeaders(body ? { "Content-Type": "application/json" } : {}),
      body: body ? JSON.stringify(body) : undefined,
      signal,
    });
  } catch (err) {
    if (err?.name === "AbortError") throw err;
    throw new CvantoAIError("Немає з'єднання. Перевірте інтернет і спробуйте ще раз", { code: "NETWORK" });
  }

  if (!response.ok) throw await toError(response);
  return response.json();
}

const qs = (params) => {
  const p = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => v != null && v !== "" && p.set(k, String(v)));
  const s = p.toString();
  return s ? `?${s}` : "";
};

export const listConversations = ({ cursor, limit } = {}, signal) =>
  request(`/conversations${qs({ cursor, limit })}`, { signal });

export const createConversation = ({ title, seedSearch } = {}, signal) =>
  request("/conversations", {
    method: "POST",
    body: { ...(title ? { title } : {}), ...(seedSearch ? { seedSearch } : {}) },
    signal,
  });

export const renameConversation = (id, title) =>
  request(`/conversations/${encodeURIComponent(id)}`, { method: "PATCH", body: { title } });

export const deleteConversation = (id) =>
  request(`/conversations/${encodeURIComponent(id)}`, { method: "DELETE" });

export const deleteAllConversations = () => request("/conversations", { method: "DELETE" });

export const getMessages = (id, { cursor, limit } = {}, signal) =>
  request(`/conversations/${encodeURIComponent(id)}/messages${qs({ cursor, limit })}`, { signal });

export const getUsage = (signal) => request("/usage", { signal });

// Next page of an Agent result set (§36/§44): the canonical intent the turn echoed + how many cards are loaded. No model call.
export const searchPage = (intent, offset, limit = 24, signal) =>
  request("/search-page", { method: "POST", body: { intent, offset, limit }, signal });

// Incremental SSE parser: feed() takes arbitrary chunks and yields complete
// { event, data } records. Comment lines (":" heartbeats) are ignored.
export function createSseParser(onEvent) {
  let buffer = "";

  const dispatch = (block) => {
    let event = "message";
    const dataLines = [];

    for (const line of block.split("\n")) {
      if (!line || line.startsWith(":")) continue;
      if (line.startsWith("event:")) event = line.slice(6).trim();
      else if (line.startsWith("data:")) dataLines.push(line.slice(5).replace(/^ /, ""));
    }

    if (!dataLines.length) return;

    try {
      onEvent(event, JSON.parse(dataLines.join("\n")));
    } catch {
      /* a malformed frame must never break the stream */
    }
  };

  return {
    feed(chunk) {
      buffer += chunk.replace(/\r\n/g, "\n");
      let idx;
      while ((idx = buffer.indexOf("\n\n")) >= 0) {
        dispatch(buffer.slice(0, idx));
        buffer = buffer.slice(idx + 2);
      }
    },
    flush() {
      if (buffer.trim()) dispatch(buffer);
      buffer = "";
    },
  };
}

/**
 * Sends a message (or a UI action such as "create_request") and streams the
 * turn. `onEvent(name, data)` receives: start, status, user_message, results,
 * text, actions, done, error. Resolves when the stream ends; rejects with a
 * CvantoAIError for HTTP-level refusals (limit reached, duplicate, ...),
 * which the server sends as ordinary JSON before any stream is opened.
 */
export async function sendMessage(conversationId, { text, action, clientMessageId }, { onEvent, signal } = {}) {
  let response;

  try {
    response = await fetch(`${BASE}/conversations/${encodeURIComponent(conversationId)}/messages`, {
      method: "POST",
      headers: await authHeaders({ "Content-Type": "application/json", Accept: "text/event-stream" }),
      body: JSON.stringify({ text, action, clientMessageId }),
      signal,
    });
  } catch (err) {
    if (err?.name === "AbortError") throw err;
    throw new CvantoAIError("Немає з'єднання. Перевірте інтернет і спробуйте ще раз", { code: "NETWORK" });
  }

  if (!response.ok) throw await toError(response);

  const parser = createSseParser((event, data) => onEvent?.(event, data));

  // Streaming where supported; a plain text() read otherwise (the frames are
  // identical, they just all arrive at once).
  if (response.body?.getReader) {
    const reader = response.body.getReader();
    const decoder = new TextDecoder();

    try {
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        parser.feed(decoder.decode(value, { stream: true }));
      }
    } catch (err) {
      if (err?.name === "AbortError") throw err;
      throw new CvantoAIError("З'єднання перервано", { code: "STREAM_INTERRUPTED" });
    }
  } else {
    parser.feed(await response.text());
  }

  parser.flush();
}

// Ordinary (deterministic, no-AI) Cvanto search — the documented fallback when
// the AI layer is unavailable. Same public endpoint the search page uses.
export async function ordinarySearch(q, { limit = 12, signal } = {}) {
  const params = new URLSearchParams({ q: String(q || ""), type: "listings", limit: String(limit) });
  const response = await fetch(`${API_BASE}/api/search/feed?${params.toString()}`, { signal });
  if (!response.ok) throw new CvantoAIError("Не вдалося виконати пошук", { status: response.status });
  const data = await response.json();
  return Array.isArray(data.items) ? data.items : [];
}
